/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, IGridSettings, IImageSettings, Image, msdyn365Commerce } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import {
    CartLine,
    ProductWarehouseInventoryAvailability,
    SalesLine
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classNames from 'classnames';
import * as React from 'react';
import { PriceComponent } from '@msdyn365-commerce/components';
import { getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { Quantity } from '../../utilities/quantity';
import { hasDeliveryOnlyError, hasPickupOnlyError, getDeliveryMethodsAllowedProperty } from '../../../../helpers/cart-validation-helpers';
import { IDeliveryOptions } from '../../../../actions/farmlands-get-delivery-options-calls.action';
// importing OOTB node module - bypass error https://stackoverflow.com/questions/41292559/could-not-find-a-declaration-file-for-module-module-name-path-to-module-nam
// tslint:disable-next-line: no-require-imports
const { getCheckoutCart } = require('@msdyn365-commerce/global-state/dist/lib/data-actions/get-checkout-cart');
import { inventoryStocks } from '../../../../modules/farmlands-cart/inventoryStore';
import { observer } from 'mobx-react'

type DeviceType = 'Mobile';

export interface IFarmlandsInventoryAvaliabilityMessages {
    clickCollectText?: string;
    deliveryText?: string;
    outOfStockText?: string;
    noBackOrder?: string;
    clickCollectOutOfStock?: string;
    deliveryOutOfStock?: string;
}

/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs
 */
export interface ICartlineResourceString {
    /** Display string for discount label */
    discountStringText: string;
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
    /** Display string for quantity label  */
    quantityDisplayString: string;
    /** Display string for quantity label  */
    inputQuantityAriaLabel: string;
    /** Aria label for the decrement button in quanitity component  */
    decrementButtonAriaLabel?: string;
    /** Aria label for the increment button in quanitity component  */
    incrementButtonAriaLabel?: string;
    /** original text screenreader */
    originalPriceText: string;
    /** current text screenreader  */
    currentPriceText: string;
    inventoryAvaliabilityMessages: IFarmlandsInventoryAvaliabilityMessages;
}

interface ICartLineState {
    cartData: any;
}

interface ICartLineData {
    cartLine: CartLine | SalesLine;
    product: SimpleProduct;
}

export type CartLineDisplayMode = 'DEFAULT' | 'COMPACT';

/**
 * ICartLineProps: The props required to render cartLineitem
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /** The flag to change the quantity component from interactible to static  */
    disableQuantityInteractivity?: boolean;
    /** The primary image url  */
    primaryImageUrl?: string;
    /** The product url  */
    productUrl?: string;
    /** GridSettings for the product image in cartLine  */
    gridSettings: IGridSettings;
    /** ImageSettings for the product image in cartLine  */
    imageSettings: IImageSettings;
    /** Boolean flag to indicate if the item is out of stock  */
    isOutOfStock?: boolean;
    /** Flag to make quantity section editable  */
    isQuantityEditable?: boolean;
    /** Max quantity for line item  */
    maxQuantity?: number;
    /** Current quantity for line item  */
    currentQuantity?: number;
    /** Resource string for the component  */
    resources: ICartlineResourceString;
    /** SalesLine flag  */
    isSalesLine?: boolean;
    /** Error message to show in place of quantity */
    errorMessage?: string;
    errorMessageOutOfStock?: string;
    /** Display mode to use */
    displayMode?: CartLineDisplayMode;
    /** Inventory information label  */
    inventoryInformationLabel?: string;
    /** Inventory information class name  */
    inventoryLabelClassName?: string;
    /** Boolean flag to indicate if cart state status is ready  */
    isCartStateReady?: boolean;
    /** Remove item button text */
    removeButtonText?: string;
    /** Workaround to pass children elements down to view */
    children?: {
        removeButton: JSX.Element;
        validationErrorRemoveButton: JSX.Element;
        deliveryOptions: IDeliveryOptions;
    };
    /** Quantity onChange callback  */
    quantityOnChange?(cartLine: CartLine, newQuantity: number): void;
}

interface IDimensionStrings {
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
}

const CartLineItemFunctions = {
    renderProductDimensions: (Product: SimpleProduct, DimensionStrings: IDimensionStrings) => {
        if (!Product || !Product.Dimensions) {
            return [];
        }

        return Product.Dimensions.map((productDimension) => {
            if (productDimension.DimensionTypeValue === 1) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions1`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-color'>
                            {DimensionStrings.colorString}:
                            <span className='name'>
                                {productDimension.DimensionValue && productDimension.DimensionValue.Value}
                            </span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 2) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions2`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-configuration'>
                            {DimensionStrings.configString}:
                            <span className='name'>
                                {productDimension.DimensionValue && productDimension.DimensionValue.Value}
                            </span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 3) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions3`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-size'>
                            {DimensionStrings.sizeString}:
                            <span className='name'>
                                {productDimension.DimensionValue && productDimension.DimensionValue.Value}
                            </span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === 4) {
                return (
                    <div key={`${Product.RecordId}ProductDimensions4`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-style'>
                            {DimensionStrings.styleString}:
                            <span className='name'>
                                {productDimension.DimensionValue && productDimension.DimensionValue.Value}
                            </span>
                        </span>
                    </div>
                );
            }
            return null;
        });
    },
    renderDiscountLines: (props: ICartLineProps) => {
        if (!props.data.cartLine.DiscountLines || props.data.cartLine.DiscountLines.length === 0) {
            return null;
        }
        return props.data.cartLine.DiscountLines.map((discountLine, index) => {
            return (
                <div key={discountLine.OfferId || index} className='msc-cart-line-item-product-discount'>
                    <span className='msc-cart-line__product-savings-label'>
                        {discountLine.OfferName ? discountLine.OfferName : ''}:
                        {` ${props.resources.discountStringText || 'Discount'} `}
                    </span>
                    <span className='msc-cart-line__promo-codes'>
                        {discountLine.DiscountCost && (
                            <>
                                <PriceComponent
                                    data={
                                        props.isSalesLine
                                            ? {
                                                price: {
                                                    // @ts-ignore
                                                    CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                                }
                                            }
                                            : {
                                                price: {
                                                    CustomerContextualPrice:
                                                        props.data.cartLine.DiscountAmountWithoutTax,
                                                    BasePrice: props.data.cartLine.DiscountAmount
                                                }
                                            }
                                    }
                                    context={props.context}
                                    id={props.id}
                                    typeName={props.typeName}
                                    className='msc-cart-line__discount-value'
                                />
                            </>
                        )}
                    </span>
                    <span>{` (${discountLine.Percentage !== undefined ? discountLine.Percentage : ''}%)`}</span>
                </div>
            );
        });
    },
    renderInventoryLabel: (props: ICartLineProps) => {
        if (!props.inventoryInformationLabel) {
            return null;
        }
        const inventoryCssName = props.inventoryLabelClassName
            ? `msc-cart-line__product-inventory-label ${props.inventoryLabelClassName}`
            : 'msc-cart-line__product-inventory-label';
        return <span className={inventoryCssName}>{props.inventoryInformationLabel}</span>;
    }
};

@observer
// tslint:disable-next-line: completed-docs
class CartLineComponent extends React.Component<ICartLineProps, ICartLineState> {
    public state: ICartLineState = {
        cartData: undefined
    };

    constructor(props: ICartLineProps) {
        super(props);
    }

    public async componentDidMount(): Promise<void> {
        if (this.props.context.request.user.token) {
            const data = await getCheckoutCart({}, this.props.context.actionContext);
            this.setState({ cartData: data });
        }
    }

    public render(): JSX.Element {
        const { children, productUrl, resources } = this.props;
        const {
            cartLine,
            cartLine: {
                LineNumber
            },
            product
        } = this.props.data;
        const { isClickCollect, lineItems } = inventoryStocks;

        // @ts-ignore
        const inventoryLineItem = lineItems.find(item => item.LineNumber === LineNumber);
        const deliveryMethodsAllowed = cartLine.ExtensionProperties?.find(item => item.Key === 'DeliveryMethodsAllowed');
        const isDeliveryMethodsAllowedDelivery = deliveryMethodsAllowed?.Value?.StringValue?.includes('Delivery');

        const hasError =
            children?.validationErrorRemoveButton &&
            children?.deliveryOptions &&
            (hasDeliveryOnlyError(cartLine, children.deliveryOptions.DeliveryOptions) ||
                hasPickupOnlyError(cartLine, children.deliveryOptions.DeliveryOptions)) ||
                    // @ts-ignore
                    (lineItems.length > 0 && inventoryLineItem && inventoryLineItem.PhysicalAvailableInventoryLevelCode === 'OOS')// && !inventoryLineItem.IsBackOrderAllowed)

        const destructDimensionStrings = {
            sizeString: resources.sizeString,
            colorString: resources.colorString,
            styleString: resources.styleString,
            configString: resources.configString
        };
        const fallbackImageUrl = getFallbackImageUrl(
            product.ItemId,
            this.props.context.actionContext.requestContext.apiSettings
        );
        const productDimensions = CartLineItemFunctions.renderProductDimensions(product, destructDimensionStrings);

        const isMobile =
            this.props.context.request &&
            this.props.context.request.device &&
            (this.props.context.request.device.Type as DeviceType) === 'Mobile';

        const _generateErrorMessage = (): JSX.Element | null => {
            if (this.props.errorMessage) {
                return (
                    <div className='msc-alert__header'>
                        <span className='msi-exclamation-triangle' />
                        <span>{this.props.errorMessage}</span>
                    </div>
                );
            }

            return null;
        };
        const _generateValidationErrorMessage = (isCompact = false) => {
            const isDeliveryOnly = hasDeliveryOnlyError(
                cartLine,
                this.props.children?.deliveryOptions.DeliveryOptions!
            );

            const deliveryMethodAllowed = getDeliveryMethodsAllowedProperty(cartLine);

            const {
                deliveryText,
                clickCollectText,
                noBackOrder,
                clickCollectOutOfStock,
                deliveryOutOfStock
            } = this.props.resources.inventoryAvaliabilityMessages || {
                clickCollectText: '',
                deliveryText: '',
                noBackOrder: '',
                clickCollectOutOfStock: '',
                deliveryOutOfStock: '',
            };

            const errorText = isDeliveryOnly ? deliveryText : clickCollectText;
            const lineItem: ProductWarehouseInventoryAvailability = lineItems.find((item: CartLine) => item.LineNumber === LineNumber) || {};
            const isOOSCC = isClickCollect && lineItem && lineItem.PhysicalAvailableInventoryLevelCode === 'OOS';
            const isOOSDel = !isClickCollect && lineItem && lineItem.PhysicalAvailableInventoryLevelCode === 'OOS';
            // @ts-ignore
            const isBackOrderAllowed = lineItem.IsBackOrderAllowed;

            if (this.props.typeName === 'farmlands-checkout') {
                return null;
            }
            else if (isCompact) {

                if(isClickCollect && deliveryMethodAllowed === 'Delivery'){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                            {errorText}
                        </span>
                    );
                }

                if(!isClickCollect && deliveryMethodAllowed === 'Pickup'){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                            {errorText}
                        </span>
                    );
                }

                if(isOOSCC && isBackOrderAllowed){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                            {clickCollectOutOfStock}
                        </span>
                    )
                }

                if(isOOSDel && isBackOrderAllowed){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                            {deliveryOutOfStock}
                        </span>
                    )
                }

                if((isOOSCC || isOOSDel) && !isBackOrderAllowed){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                            {noBackOrder}
                        </span>
                    )
                }

                return (
                    <span className='msc-cart-line__validation-error-text'>
                        {errorText}
                    </span>
                );
            }
            else {
                const {
                    deliveryOnly,
                    clickCollectOnly,
                    outOfStockCC,
                    outOfStockDel,
                    oosNoBackOrder,
                    // @ts-ignore
                } = this.props.cartLineDeliveryMethodsText;

                const extendedText = isDeliveryOnly
                    ? (deliveryOnly || `Please select 'Delivery' or `)
                    : (clickCollectOnly || `Please select 'Click & Collect' or `);

                if(isClickCollect && deliveryMethodAllowed === 'Delivery'){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                        {extendedText} {this.props.children?.validationErrorRemoveButton}
                    </span>
                    );
                }

                if(!isClickCollect && deliveryMethodAllowed === 'Pickup'){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                        {extendedText}
                            {this.props.children?.validationErrorRemoveButton}
                        </span>
                    );
                }

                if(isOOSCC && isBackOrderAllowed){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                            {outOfStockCC}
                        </span>
                    )
                }

                if(isOOSDel && isBackOrderAllowed){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                            {outOfStockDel}
                        </span>
                    )
                }

                if(isOOSCC && !isBackOrderAllowed){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                            {oosNoBackOrder} {this.props.children?.validationErrorRemoveButton}
                        </span>
                    )
                }

                if((isOOSDel) && (isBackOrderAllowed == false)){
                    return (
                        <span className='msc-cart-line__validation-error-text'>
                            {oosNoBackOrder} {this.props.children?.validationErrorRemoveButton}
                        </span>
                    )
                }

                return (
                    <span className='msc-cart-line__validation-error-text'>
                        {extendedText}
                        {this.props.children?.validationErrorRemoveButton}
                    </span>
                );
            }

        };

        const _updateQuantity = (value: number) => {
            if (this.props.quantityOnChange) {
                this.props.quantityOnChange(cartLine, value);
            }
        };

        const _generateQuantityAndPrice = (renderPrice: boolean = true): JSX.Element[] | null => {
            const nodes = [];
            const errorMessage = _generateErrorMessage();

            if (this.props.isOutOfStock) {
                if (errorMessage) {
                    return [errorMessage];
                }

                return null;
            }

            if (this.props.isQuantityEditable) {
                if (errorMessage) {
                    nodes.push(errorMessage);
                }
                nodes.push(
                    <Quantity
                        quantityClasses='msc-cart-line__quantity-select'
                        id={`msc-cart-line__quantity_${product.RecordId}`}
                        max={this.props.maxQuantity || 10}
                        currentCount={this.props.currentQuantity}
                        // tslint:disable-next-line: react-this-binding-issue
                        onChange={_updateQuantity}
                        inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                        decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                        incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                        key={cartLine.LineId}
                    />
                );
            } else {
                nodes.push(
                    <div
                        className={classNames('msc-cart-line__quantity', {
                            'single-quantity': cartLine.Quantity === 1
                        })}
                    >
                        <label className='quantity-label'>{resources.quantityDisplayString}</label>
                        {errorMessage}
                        <span className='quantity-value'>{cartLine.Quantity}</span>
                    </div>
                );
            }

            if (renderPrice) {
                const totalPriceIncGST = cartLine.ExtensionProperties?.find(
                    (property: any) => property.Key === 'BasePriceWithGST'
                )?.Value?.DecimalValue;

                const totalSpecialPriceIncGST =
                    cartLine.ExtensionProperties?.find((property: any) => property.Key === 'NetAmountIncGST')?.Value
                        ?.DecimalValue || cartLine.TotalAmount;

                nodes.push(
                    <div className='msc-cart-line__product-price'>
                        <PriceComponent
                            data={{
                                price: {
                                    CustomerContextualPrice: totalSpecialPriceIncGST,
                                    BasePrice: totalPriceIncGST
                                }
                            }}
                            context={this.props.context}
                            id={this.props.id}
                            typeName={this.props.typeName}
                            className='discount-value'
                            originalPriceText={resources.originalPriceText}
                            currentPriceText={resources.currentPriceText}
                        />
                    </div>
                );
            }

            return nodes;
        };

        // Workaround for sanitizing image urls containing custom options in the image filename (which cause the image load to fail)
        const sanitizeImageUrl = (imageUrl: string | undefined): string => {
            const url = imageUrl || '';
            const split = decodeURIComponent(url).split('^'); // Split on this decoded URI string (only image urls with custom options should have this character)
            if (split.length > 1) {
                // If one of the image urls with custom options, append the start and end items of the split to get the normal filename
                return split[0].trim() + split[split.length - 1].trim();
            }

            // Else, return url verbatim.
            return url;
        };

        if (this.props.displayMode === 'COMPACT') {
            return (
                <div className={`msc-cart-line msc-cart-line__compact${hasError && this.props.typeName !== 'farmlands-checkout' ? ' msc-cart-line--error' : ''}`}>
                    {hasError && this.props.typeName !== 'farmlands-checkout' && (
                        <div className='msc-cart-line__validation-error'>
                            <div className='msc-cart-line__validation-error-icon material-icons'>error_outline</div>
                            {_generateValidationErrorMessage(true)}
                        </div>
                    )}
                    <div className='msc-cart-line__content-wrapper'>
                        <div className='msc-cart-line__product-image'>
                            <Image
                                src={sanitizeImageUrl(this.props.primaryImageUrl)}
                                fallBackSrc={fallbackImageUrl}
                                altText={product.Name}
                                gridSettings={this.props.gridSettings}
                                imageSettings={this.props.imageSettings}
                                loadFailureBehavior='empty'
                            />
                        </div>
                        <div className='msc-cart-line__content'>
                            <a className='msc-cart-line__product-title' href={productUrl}>
                                {product.Name}
                            </a>
                            <div className='msc-cart-line__product-sku'>{`SKU: ${product.ItemId}`}</div>
                            <div className='msc-cart-line__product-variants'>
                                {productDimensions.length > 0 ? <>{productDimensions}</> : ''}
                                {_generateQuantityAndPrice(false)}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }


        return (
            <div className={`msc-cart-line${hasError ? ' msc-cart-line--error' : ''}`}>
                {hasError && (
                    <div className='msc-cart-line__validation-error'>
                        <div className='msc-cart-line__validation-error-icon material-icons'>error_outlinee</div>
                        {_generateValidationErrorMessage()}
                    </div>
                )}
                <div className='msc-cart-line__content-wrapper'>
                    <div className='msc-cart-line__product-image'>
                        <Image
                            src={sanitizeImageUrl(this.props.primaryImageUrl)}
                            altText={product.Name}
                            gridSettings={this.props.gridSettings}
                            imageSettings={this.props.imageSettings}
                            loadFailureBehavior='empty'
                        />
                        {
                            isMobile && inventoryStocks.isClickCollect && (
                                <div className='mobile-inventory-label-container'>
                                    <StockLabel
                                        inventoryLineItem={inventoryLineItem}
                                        notDeliveryOnly={!isDeliveryMethodsAllowedDelivery}
                                    />
                                </div>
                            )
                        }
                    </div>
                    <div className='msc-cart-line__product-details'>
                        <div className='msc-cart-line__center-content'>
                            <div className='msc-cart-line__product'>
                                <a className='msc-cart-line__product-title' href={productUrl}>
                                    {product.Name}
                                </a>
                                <span className='msc-cart-line__product-sku'>{`SKU: ${product.ItemId}`}</span>
                                {productDimensions.length > 0 && (
                                    <div className='msc-cart-line__product-variants'>{productDimensions}</div>
                                )}
                            </div>
                        </div>
                        {
                            !isMobile && inventoryStocks.isClickCollect && (
                                <StockLabel
                                    inventoryLineItem={inventoryLineItem}
                                    notDeliveryOnly={!isDeliveryMethodsAllowedDelivery}
                                />
                            )
                        }
                        <div className='msc-cart-line__end-content'>
                            <div className='msc-cart-line__quantity-label'>Qty:</div>
                            <div className='msc-cart-line__quantity-price-wrapper'>{_generateQuantityAndPrice()}</div>
                            <div className='msc-cart-line__remove-buttom-wrapper'>
                                {this.props.children?.removeButton}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const StockLabel = (props: any): JSX.Element | null => {
    const {
        PhysicalAvailableInventoryLevelCode,
        PhysicalAvailableInventoryLevelLabel
    } = props.inventoryLineItem;

    let inventoryClassName: string = '';
    let icon: JSX.Element | undefined = undefined;

    switch(PhysicalAvailableInventoryLevelCode) {
        case 'AVAIL':
            inventoryClassName = 'has-stock';
            icon = <Tick />;
            break;
        case 'LOW':
            inventoryClassName = 'low-stock';
            icon = <ExclamationMark />;
            break;
        case 'OOS':
            inventoryClassName = 'not-has-stock';
            icon = <Cross />;
            break;
        default:
            return null;
    }

    // if (!props.notDeliveryOnly) {
    //     return (
    //         <p className='not-avaliable'></p>
    //     )
    // }

    return (
        <p className={inventoryClassName}>
            {icon}
            {PhysicalAvailableInventoryLevelLabel}
        </p>
    )
}

const Tick = (): JSX.Element => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-check" viewBox="0 0 16 16">
        <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
    </svg>
)

const Cross = (): JSX.Element => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-x" viewBox="0 0 16 16">
        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
    </svg>
)

const ExclamationMark = (): JSX.Element => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-exclamation-lg" viewBox="0 0 16 16">
        <path d="M7.005 3.1a1 1 0 1 1 1.99 0l-.388 6.35a.61.61 0 0 1-1.214 0L7.005 3.1ZM7 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z"/>
    </svg>
)

// @ts-ignore
export const CartLineItemComponent: React.FunctionComponent<ICartLineProps> = msdyn365Commerce.createComponentOverride<ICartline>(
    'CartLineItem',
    { component: CartLineComponent, ...CartLineItemFunctions }
);

export default CartLineItemComponent;
